import { map } from 'underscore';
import React, { useCallback, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
  Dropdown,
  Label,
  Option,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogTrigger,
  Button,
  useRestoreFocusTarget,
} from '@fluentui/react-components';
import {
  MessageBar,
  MessageBarBody,
  MessageBarTitle
} from '@fluentui/react-message-bar';
import { AdGroup } from '@bingads-webui/campaign-odata-schemas';
import { RemainingCharsValidatedTextField } from '@bingads-webui-campaign-react/remaining-chars-hint';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { ValidatedDynamicSearchAdSetting } from './validated-dsa-setting';

const getStyles = () =>
  ({
    adGroupBasicSetting: {
      '& .ad-group-type-label': {
        display: 'flex',
        alignItems: 'center',
      },
      '& .ad-group-name-label': {
        marginTop: '20px',
        display: 'flex',
        alignItems: 'center',
      },
      '& .message-bar': {
        marginTop: '20px',
      },
    },
  });

export const AdGroupTypeSelector = ({
  allowAdGroupTypeChange, currentAdGroupType, onSwitchAdGroupType, adGroupTypes, adGroupTypeStrings,
}) => {
  const classes = useDefaultStyles(getStyles);
  const i18n = useContext(I18nContext);
  const activity = useContext(ActivityContext);

  const newAdGroupType = useRef(currentAdGroupType);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const restoreFocusTargetAttribute = useRestoreFocusTarget();

  const onOptionSelect = useCallback((_evt, option) => {
    if (option.optionValue !== currentAdGroupType) {
      setIsDialogOpen(true);
      newAdGroupType.current = option.optionValue;
      activity.signal(`onAdGroupTypeChangeDropdownOptionClicked - ${option.optionValue}`);
    }
  }, [activity, currentAdGroupType]);

  const onDialogSwitchClick = useCallback(() => {
    setIsDialogOpen(false);
    onSwitchAdGroupType(newAdGroupType.current);
  }, [onSwitchAdGroupType]);

  return (
    <div className={classnames('ad-group-type-selector-container', classes.adGroupTypeSelectorContainer)}>
      <div className="ad-group-type-label">
        <Label weight="semibold">
          {i18n.getString(_TL_('Ad group type'))}
        </Label>
        <HelpPopup
          topic="pop_BA_Search_AdGroupType"
          aria-label={i18n.getString(_TL_('Click to learn more about Ad group type'))}
        />
      </div>
      <Dropdown
        className="ad-group-type-dropdown"
        aria-label={i18n.getString(_TL_('Ad group selector'))}
        value={i18n.getString(adGroupTypeStrings[currentAdGroupType] || '')}
        selectedOptions={[currentAdGroupType]}
        onOptionSelect={onOptionSelect}
        disabled={!allowAdGroupTypeChange}
      >
        {map(adGroupTypes, adGroupType => (
          <Option
            key={adGroupType}
            value={adGroupType}
            {...restoreFocusTargetAttribute}
          >
            {i18n.getString(adGroupTypeStrings[adGroupType])}
          </Option>
          ))}
      </Dropdown>
      <Dialog
        open={isDialogOpen}
        onOpenChange={(_evt, data) => {
            setIsDialogOpen(data.open);
          }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{i18n.getString(_TL_('Are you sure you want to change the ad group type?'))}</DialogTitle>
            <DialogContent>
              {/* eslint-disable-next-line react/no-danger */}
              <div dangerouslySetInnerHTML={{ __html: i18n.getString(_TL_("Your previous ad group won't be saved. Note: You can always create a new ad group later in <b>Campaigns > Ad groups</b>.")) }} />
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={onDialogSwitchClick}>{i18n.getString(_TL_('Yes'))}</Button>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">{i18n.getString(_TL_('No'))}</Button>
              </DialogTrigger>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

AdGroupTypeSelector.propTypes = {
  allowAdGroupTypeChange: PropTypes.bool.isRequired,
  currentAdGroupType: PropTypes.string.isRequired,
  onSwitchAdGroupType: PropTypes.func.isRequired,
  adGroupTypes: PropTypes.shape({
    SearchStandard: PropTypes.string.isRequired,
    SearchDynamic: PropTypes.string.isRequired,
  }).isRequired,
  adGroupTypeStrings: PropTypes.shape({}).isRequired,
  isEditingScenario: PropTypes.bool.isRequired,
};


export const ValidatedStandardAdGroup = ({
  adGroup,
  currentAdGroupType,
  onSwitchAdGroupType,
  adGroupTypes,
  adGroupTypeStrings,
  isMixedMode,
  isEditingScenario,
}) => {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getStyles);

  return (
    <div className={classnames('adGroup-basic-section', classes.adGroupBasicSetting)}>
      {isMixedMode && (<AdGroupTypeSelector
        allowAdGroupTypeChange={adGroup && adGroup.allowAdGroupTypeChange}
        currentAdGroupType={currentAdGroupType}
        onSwitchAdGroupType={onSwitchAdGroupType}
        adGroupTypes={adGroupTypes}
        adGroupTypeStrings={adGroupTypeStrings}
      />)}
      <div className="ad-group-name-label">
        <Label weight="semibold">
          {i18n.getString(_TL_('Ad group name'))}
        </Label>
        <HelpPopup
          topic="pop_BA_Search_AdGroupName"
          aria-label={i18n.getString(_TL_('Click to learn more about Ad group name'))}
        />
      </div>
      <RemainingCharsValidatedTextField
        placeholder={i18n.getString(_TL_('Enter a name for your ad group'))}
        id="input-ad-group-name"
        validationProps={{
          data: adGroup,
          field: 'Name',
          schema: AdGroup,
        }}
        i18n={i18n}
      />
      {!isEditingScenario && (
        <MessageBar className="message-bar">
          <MessageBarBody>
            <MessageBarTitle>{i18n.getString(_TL_('Note'))}</MessageBarTitle>
            {i18n.getString(_TL_('Standard ad groups contain Multimedia ads and/or Responsive Search ads.'))}
          </MessageBarBody>
        </MessageBar>
      )}
    </div>
  );
};

ValidatedStandardAdGroup.propTypes = {
  adGroup: PropTypes.shape({
    allowAdGroupTypeChange: PropTypes.bool,
  }).isRequired,
  currentAdGroupType: PropTypes.string.isRequired,
  onSwitchAdGroupType: PropTypes.func.isRequired,
  adGroupTypes: PropTypes.shape({
    SearchStandard: PropTypes.string.isRequired,
    SearchDynamic: PropTypes.string.isRequired,
  }).isRequired,
  adGroupTypeStrings: PropTypes.shape({}).isRequired,
  isMixedMode: PropTypes.bool.isRequired,
  isEditingScenario: PropTypes.bool.isRequired,
};

export const ValidatedDynamicAdGroup = ({
  adGroup,
  currentAdGroupType,
  onSwitchAdGroupType,
  adGroupTypes,
  adGroupTypeStrings,
}) => {
  const { showDsaSettings, disableEditDsaSettings } = adGroup || {};
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getStyles);

  return (
    <div className={classnames('adGroup-basic-section', classes.adGroupBasicSetting)}>
      <AdGroupTypeSelector
        allowAdGroupTypeChange={adGroup && adGroup.allowAdGroupTypeChange}
        currentAdGroupType={currentAdGroupType}
        onSwitchAdGroupType={onSwitchAdGroupType}
        adGroupTypes={adGroupTypes}
        adGroupTypeStrings={adGroupTypeStrings}
      />
      <div className="ad-group-name-label">
        <Label weight="semibold">
          {i18n.getString(_TL_('Ad group name'))}
        </Label>
        <HelpPopup
          topic="pop_BA_Search_AdGroupName"
          aria-label={i18n.getString(_TL_('Click to learn more about Ad group name'))}
        />
      </div>
      <RemainingCharsValidatedTextField
        placeholder={i18n.getString(_TL_('Enter a name for your ad group'))}
        id="input-ad-group-name"
        validationProps={{
          data: adGroup,
          field: 'Name',
          schema: AdGroup,
        }}
        i18n={i18n}
      />
      {showDsaSettings && (
        <ValidatedDynamicSearchAdSetting
          data={adGroup}
          disabled={disableEditDsaSettings}
        />
      )}
    </div>
  );
};

ValidatedDynamicAdGroup.propTypes = {
  adGroup: PropTypes.shape({
    allowAdGroupTypeChange: PropTypes.bool,
  }).isRequired,
  currentAdGroupType: PropTypes.string.isRequired,
  onSwitchAdGroupType: PropTypes.func.isRequired,
  adGroupTypes: PropTypes.shape({
    SearchStandard: PropTypes.string.isRequired,
    SearchDynamic: PropTypes.string.isRequired,
  }).isRequired,
  adGroupTypeStrings: PropTypes.shape({}).isRequired,
};

